/**
 * Chart → table derivations (ADR-0038-adjacent UX work; the accessible counterpart to `charts.tsx`).
 *
 * Each function turns a chart's own inputs into the exact numbers it draws — no rounding the visual
 * does not do, no figure the chart does not show. That equivalence is the whole contract: the table is
 * the COMPLETE path to the data and the chart is the fast one, so if they disagree the table is wrong.
 *
 * Pure and exported so the equivalence is testable directly, which matters more than usual here — the
 * web package has no DOM harness, so a table built inside a component would be asserted only through
 * rendered markup.
 */

import type { CategoryDetail, InvestmentScore, IrrMatrix, LenderSummary } from '../api.js';
import { multiple, percentFromRatio } from '../format.js';

/**
 * A rent delta reads as a SIGNED change ("-5%", "0%"), because in this matrix the sign is the whole
 * point — the reader is asking what happens if rents come in under the proforma.
 */
function signedRentDelta(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const pct = value * 100;
  const rounded = Math.round(pct * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

export interface TableData {
  caption: string;
  columns: string[];
  rows: (string | number)[][];
  rowHeaders?: boolean;
}

/**
 * The rent × cap IRR matrix. Rows are rent deltas, columns exit caps — the same orientation the
 * heatmap draws, so a reader moving between the two is not transposing in their head. The base case is
 * marked in text because in the chart it is marked by a gold ring, and a ring is not available to a
 * screen reader.
 */
export function irrMatrixTable(matrix: IrrMatrix): TableData {
  const caps = matrix.exitCaps ?? [];
  const rows = matrix.rows ?? [];
  return {
    caption: 'Project IRR by rent change and exit cap rate',
    columns: ['Rent change', ...caps.map((c) => `Exit cap ${percentFromRatio(c, 2)}`)],
    rowHeaders: true,
    rows: rows.map((row) => [
      signedRentDelta(row[0]?.rentDelta),
      ...row.map((cell) => {
        const irr = percentFromRatio(cell.projectIRR, 1);
        const band = cell.band ? ` (${cell.band})` : '';
        return `${irr}${band}${cell.isBaseCase ? ' — base case' : ''}`;
      }),
    ]),
  };
}

/**
 * One heatmap cell, said in full: which scenario it is, and what it returns.
 *
 * The heatmap prints each cell's IRR inside the cell, so the number was never the gap — WHICH SCENARIO
 * the number belongs to was. A reader had to trace a cell back to a row header and a column header to
 * learn it was the "-5% rent, 5.5% exit cap" case. This is the string a pointer gets on hover.
 *
 * It lives HERE, beside `irrMatrixTable`, on purpose: the tooltip and the table describe the same cell,
 * and if they described it in different words (or rounded it differently) the chart's fast path and its
 * complete path would disagree. Same vocabulary, same formatters, one place to change.
 */
export function irrCellLabel(cell: {
  rentDelta?: number | null;
  exitCap?: number | null;
  projectIRR?: number | null;
  band?: string;
  isBaseCase?: boolean;
}): string {
  const scenario = `Rent ${signedRentDelta(cell.rentDelta)} · exit cap ${percentFromRatio(
    cell.exitCap,
    2,
  )}`;
  const band = cell.band ? ` (${cell.band})` : '';
  const base = cell.isBaseCase ? ' — base case' : '';
  return `${scenario} → IRR ${percentFromRatio(cell.projectIRR, 1)}${band}${base}`;
}

/** A category is either a bare number (historical runs) or a `CategoryDetail`. Read either. */
function categoryScore(v: number | CategoryDetail | undefined): number | undefined {
  if (v === undefined) return undefined;
  return typeof v === 'number' ? v : v.score;
}
function categoryDetail(v: number | CategoryDetail | undefined): CategoryDetail | undefined {
  return v !== undefined && typeof v === 'object' ? v : undefined;
}

/**
 * The six category scores behind the radar. The radar encodes each axis as a fraction of its own max,
 * which is unreadable as a number — so the table gives the applied score, the category's weight, and
 * the ceiling the engine says is reachable.
 */
export function categoryTable(
  score: InvestmentScore,
  categories: { key: string; label: string; max: number }[],
): TableData {
  const cs = score.categoryScores ?? {};
  return {
    caption: 'Scorecard by category',
    columns: ['Category', 'Score', 'Weight', 'Reachable', 'Fixability'],
    rowHeaders: true,
    rows: categories
      .filter((c) => categoryScore(cs[c.key as keyof typeof cs]) !== undefined)
      .map((c) => {
        const value = categoryScore(cs[c.key as keyof typeof cs]);
        const detail = categoryDetail(cs[c.key as keyof typeof cs]);
        return [
          c.label,
          value ?? '—',
          c.max,
          detail?.potentialScore ?? '—',
          detail?.fixability ?? '—',
        ];
      }),
  };
}

/**
 * The lender hurdles behind the bullet bars: the figure, the floor or cap it is measured against, and
 * whether it clears. The chart says pass/fail with position and colour; the table says it in words.
 */
export function lenderTable(lender: LenderSummary): TableData {
  const rows: (string | number)[][] = [];
  if (lender.dscr != null) {
    rows.push([
      'DSCR',
      multiple(lender.dscr, 2),
      lender.dscrFloor != null
        ? `floor ${multiple(lender.dscrFloor, 2)}${
            lender.dscrFloorBasis ? ` — ${lender.dscrFloorBasis.label}` : ''
          }`
        : '—',
      (lender.dscrPass === undefined ? '—' : lender.dscrPass ? 'clears' : 'short') +
        (lender.dscrReserveWithheld && lender.dscrPass != null
          ? ' — unverified (reserve on a contradicted unit count)'
          : ''),
    ]);
  }
  if (lender.constructionLTC != null) {
    rows.push([
      'Loan-to-cost',
      percentFromRatio(lender.constructionLTC, 1),
      lender.ltcCap != null ? `cap ${percentFromRatio(lender.ltcCap, 1)}` : '—',
      lender.ltcPass === undefined ? '—' : lender.ltcPass ? 'clears' : 'over',
    ]);
  }
  if (lender.debtYield != null) {
    rows.push(['Debt yield', percentFromRatio(lender.debtYield, 2), '—', '—']);
  }
  if (lender.refiLTV != null) {
    rows.push(['Refi LTV', percentFromRatio(lender.refiLTV, 1), '—', '—']);
  }
  return {
    caption: 'Lender tests',
    columns: ['Test', 'This deal', 'Requirement', 'Result'],
    rowHeaders: true,
    rows,
  };
}

/** The data-quality donut in words: how many inputs the engine computed vs could not. */
export function dataQualityTable(computed: string[], unavailable: string[]): TableData {
  return {
    caption: 'Which inputs the engine could compute',
    columns: ['Input', 'Status'],
    rowHeaders: true,
    rows: [
      ...computed.map((f) => [f, 'computed'] as (string | number)[]),
      ...unavailable.map((f) => [f, 'not available'] as (string | number)[]),
    ],
  };
}
